import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SilverInventorySelectedTab, ReportSilverInventoryReceivedListingFilters, ReportsSilverInventorySelectedTab, TSilverReduxState } from '~types/redux/state';
import {
    TBuyOrSellMultipleSilverParams,
    TCreateSilverConversionReduxParams,
    TCreateInventoryParamsV2,
    TCreateTransactionDraftReduxParams,
    TCreateSilverTransactionDraftResponse,
    TGetInventoryFilterAPIResponse,
    TGetInventoryFilterParams,
    TGetInventoryListingAPIResponse,
    TGetSilverInventoryListingFilters,
    TGetInventoryTransactionListingAPIResponse,
    TGetSilverInventoryTransactionListingFilters,
    TGetInventoryTransferListingAPIResponse,
    TGetInventoryTransferListingFilters,
    TGetProductFilterAPIResponse,
    TGetSerialNumberFilterAPIResponse,
    TGetSerialNumberFilterParams,
    TGetSerialNumberListingFilter,
    TGetSerialNumberListingAPIResponse,
    TGetTotalNetValueOrQuantityReduxParams,
    TGetSilverTransactionListingAPIResponse,
    TGetSilverTransactionListingFilters,
    TGetSilverTransactionPricePerGramValueReduxParam,
    TTransferInventoryParams,
    TUpdateEarmarkDetailsReduxParam,
    TCreateEarmarkConversionReduxParams,
    TGetEarmarkConversionListingAPIResponse,
    TGetEarmarkConversionListingFilters,
    TGetEarmarkConversionListingReduxParams,
    TGetInventoryUsageAPIResponse,
    TGetSilverPricesAPIResponse,
    TSilverTransactionListing,
    TSilverConversionFiltersRedux,
    TSilverConversionListingAPIResponse,
    TGetSilverConversionAmountParams,
    TTransactionSilverListing,
    TInventorySilverTransactionListing,
} from '~types/feat/silver';
import { FundTypeEnum, MetalTypeEnum, SilverTypeEnum, TransactionActionEnum, TransactionStatusEnum, TransferStatusEnum } from '~utils/enums';
import { TUpdateInventoryTransactionStatusReduxParams, TUpdateTransactionStatusReduxParams, TupdateTransferStatusReduxParams } from '~types/feat/approval';
import dayjs from 'dayjs';

const getFirstDayOfMonth = () => {
    const today = dayjs();

    const firstDay = today.startOf('month');

    return firstDay.toISOString();

};

const getTodaysDate = () => {
    const today = dayjs().endOf('day').toISOString();

    return today;
};

export const initialState: TSilverReduxState = {
    loadings: {
        getAllProducts: false,
        getAllInventories: false,
        getAllSerialNumbers: false,
        getBranchInventories: false,
        // buy and sell silver
        getTransactionListing: false,
        getSilverPrices: false,
        createBuySilverTransaction: false,
        createSellSilverTransaction: false,
        getTransactionReceipt: false,
        getTransactionPricePerGramValue: false,
        // silver inventory
        getInventoryTransactionListing: false,
        getInventoryListing: false,
        createInventory: false,
        createTransactionDraft: false,
        transferInventory: false,
        exportInventoryTransactionListing: false,
        exportSilverTransactionListing: false,
        updateInventoryTransactionStatus: false,
        updateTransactionStatus: false,
        getTotalNetValueOrQuantity: false,
        // transfers
        getInventoryTransferListing: false,
        updateInventoryTransferStatus: false,
        getSerialNumberListing: false,
        //silver inventory report
        getSilverInventoryReportReceivedListing: false,
        exportInventoryReceivedListing: false,
        getSilverInventoryReportDeliveredListing: false,
        exportInventoryDeliveredListing: false,
        getBranchInventoryListing: false,
        //Conversion
        getSilverConversionListing: false,
        getSilverConversionAmount: false,
        createSilverConversion: false,
        updateEarmarkDetails: false,
        createEarmarkConversion: false,
        getEarmarkConversionListing: false,
        getInventoryUsage: false,
    },
    buyAndSellSilver: {
        transactionListing: {
            index: 0,
            maxIndex: 0,
            filters: {
                name: '',
                idNo: '',
                action: '',
                status: '',
                dateFrom: getFirstDayOfMonth(),
                dateTo: getTodaysDate(),
                branchName: '',
            },
            data: [],
        },
        silverPrices: {
            origin: '',
            gbuy: '',
            gsell: '',
            epoch: 0,
            proof: {
                crc: '',
                dsig: '',
            },
            validFrom: '',
            validUntil: '',
        },
        transactionType: TransactionActionEnum['Bank Sell'],
        selectedTransaction: undefined,
    },
    silverInventory: {
        inventoryTransactionListing: {
            index: 0,
            maxIndex: 0,
            filters: {
                branchCode: '',
                branchName: '',
                productId: 0,
                status: '',
                productName: '',
                action: '',
                dateFrom: getFirstDayOfMonth(),
                dateTo: getTodaysDate(),
            },
            data: [],
        },
        inventoryListing: {
            index: 0,
            maxIndex: 0,
            filters: {
                branchName: '',
                branchCode: '',
                silverType: '',
            },
            data: [],
            selectedTab: SilverInventorySelectedTab.IslamicPhysical,
        },
        inventoryTransferListing: {
            index: 0,
            maxIndex: 0,
            filters: {
                branchFrom: 0,
                branchTo: 0,
                dateFrom: getFirstDayOfMonth(),
                dateTo: getTodaysDate(),
            },
            data: [],
        },
    },
    silverInventoryReport: {
        selectedTab: ReportsSilverInventorySelectedTab.BranchInventory,
        branchInventory: {
            index: 0,
            maxIndex: 0,
            // dalam ReportBranchInventoryRedux its branchcode
            filters: {
                branchCode: '',
            },
            data: [],

        },
        inventoryReceived: {
            index: 0,
            maxIndex: 0,
            filters: {
                branchName: '',
                branchCode: '',
                fundType: '',
                productName: '',
                serialNo: '',
            },
            data: [],
        },
        inventoryDelivered: {
            index: 0,
            maxIndex: 0,
            filters: {
                branchName: '',
                branchCode: '',
                fundType: '',
                productName: '',
                serialNo: '',
            },
            data: [],
        },
    },
    serialNumber: {
        serialNumberListing: {
            index: 0,
            maxIndex: 0,
            filters: {
                serialNo: undefined,
            },
            data: [],
        },
    },
    allProducts: [],
    allInventories: [],
    allSerialNumbers: [],
    branchInventories: [],
    createTransactionSummary: {
        draftId: 0,
        customerId: 0,
        action: TransactionActionEnum['Bank Sell'],
        baseAPIPrice: 0,
        items: [{
            createdAt: '',
            updatedAt: '',
            id: 0,
            draftId: 0,
            productId: 0,
            silverPrice: 0,
            amount: '',
            totalNetValue: 0,
            baseAPIPrice: 0,
            action: TransactionActionEnum['Bank Sell'],
            userId: 0,
            totalNetValueWithoutDiscount: 0,
            serialNo: [],
        }],
    },
    params: {
        buyOrSellSilverParams: {
            customerId: 0,
            name: '',
            idNo: '',
            CIFNo: '',
            branchName: '',
            branchCode: '',
            baseAPIPrice: '',
            region: '',
            products: [{
                productId: 0,
                productName: '',
                silverType: SilverTypeEnum.PaperSilver,
                availableQuantity: 'Please select customer first',
                amount: '',
                serialNo: '',
                silverAmountMYR: '',
                totalNetValue: '',
                discountId: 0,
                discountCode: '',
                discountName: '',
            }],
            fundType: FundTypeEnum.Islamic,
            salesPersonnelName: '',
            referralName: '',
            isEarmarkTransaction: false,
            earmarkEndDate: undefined,
            projectName: undefined,
        },
        createInventoryParams: {
            branchName: '',
            branchCode: '',
            products: [{
                productId: 0,
                productName: undefined,
                amount: 0,
                serialNo: '',
            }],
            metalType: MetalTypeEnum.Gold,
        },
        transferInventoryParams: {
            branchToCode: '',
            branchToName: '',
            branchFromCode: '',
            branchFromName: '',
            productId: 0,
            productName: '',
            amount: '',
            serialNo: [],
            metalType: MetalTypeEnum.Gold,
        },
        inventoryFilterParams: {
            branchCode: '',
            branchName: '',
            metalType: MetalTypeEnum.Gold,
        },
        serialNumberFilterParams: {
            action: undefined,
            branchCode: undefined,
            customerId: undefined,
        },
    },
    silverConversion: {
        index: 0,
        maxIndex: 0,
        data: [],
        filters: {
            customerName: '',
            idNo: '',
            branchName: '',
            status: '',
            dateTo: '',
            dateFrom: '',
        },
        silverConversionAmount: undefined,
    },
    earmarkConversionListing: {
        index: 0,
        maxIndex: 0,
        filters: {
            branchId: '',
            customerName: '',
            idNo: '',
            branchName: '',
            status: '',
            dateFrom: '',
            dateTo: '',
        },
        data: [],
    },
    inventoryUsage: {
        totalInventoryStock: 0,
        totalInventoryUsed: 0,
        totalInventoryBalance: 0,
    },
    modals: {
        isFormFilled: false,
        isConfirmationModalOpen: false,
        isBuyOrSellSilverModalOpen: false,
        isTransactionDetailsModalOpen: false,
        isInventoryListingModalOpen: false,
        isCreateInventoryModalOpen: false,
        isTransferInventoryModalOpen: false,
        isCreateSilverConversionModalOpen: false,
        confirmUpdateInventoryTransactionStatusModalIsOpen: false,
        confirmUpdateTransactionStatusModalIsOpen: false,
        confirmUpdateInventoryTransfersStatusModalIsOpen: false,
        transactionProductListingModalIsOpen: false,
    },
    inventoryTransactionListingForExport: [],
    inventoryReceivedListingForExport: [],
    inventoryDeliveredListingForExport: [],
    silverTransactionListingForExport: [],
    tempUpdateInventoryTransactionState: {
        inventoryLogId: 0,
        status: TransactionStatusEnum.Pending,
    },
    tempUpdateTransactionState: {
        transactionId: 0,
        status: TransactionStatusEnum.Pending,
    },
    tempUpdateInventoryTransfersState: {
        id: 0,
        status: TransferStatusEnum.PendingDeliver,
    },
    errors: {
        getAllProducts: '',
        getAllInventories: '',
        getAllSerialNumbers: '',
        getBranchInventories: '',
        // buy and sell silver
        getTransactionListing: '',
        getSilverPrices: '',
        createBuySilverTransaction: '',
        createSellSilverTransaction: '',
        getTransactionReceipt: '',
        getTransactionPricePerGramValue: '',
        // silver inventory
        getInventoryTransactionListing: '',
        getInventoryListing: '',
        createInventory: '',
        createTransactionDraft: '',
        transferInventory: '',
        exportInventoryTransactionListing: '',
        exportSilverTransactionListing: '',
        updateInventoryTransactionStatus: '',
        updateTransactionStatus: '',
        getTotalNetValueOrQuantity: '',
        // transfer
        getInventoryTransferListing: '',
        updateInventoryTransferStatus: '',
        getSerialNumberListing: '',
        //silver inventory report
        getSilverInventoryReportReceivedListing: '',
        exportInventoryReceivedListing: '',
        getSilverInventoryReportDeliveredListing: '',
        exportInventoryDeliveredListing: '',
        getBranchInventoryListing: '',
        // Conversion
        getSilverConversionListing: '',
        getSilverConversionAmount: '',
        createSilverConversion: '',
        updateEarmarkDetails: '',
        createEarmarkConversion: '',
        getEarmarkConversionListing: '',
        getInventoryUsage: '',
    },
};

const silverSlice = createSlice({
    name: 'silver',
    initialState,
    reducers: {
        // ! buy and sell silver
        silverGetTransactionListingAttempt: (state) => {
            state.loadings.getTransactionListing = true;
            state.errors.getTransactionListing = '';
        },
        silverGetTransactionListingSuccess: (state, action: PayloadAction<TGetSilverTransactionListingAPIResponse>) => {
            state.loadings.getTransactionListing = false;
            state.errors.getTransactionListing = '';
            state.buyAndSellSilver.transactionListing = {
                filters: state.buyAndSellSilver.transactionListing.filters,
                ...action.payload,
            };
        },
        silverGetTransactionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getTransactionListing = false;
            if (action.payload) state.errors.getTransactionListing = action.payload;
        },
        silverGetSilverPricesAttempt: (state) => {
            state.loadings.getSilverPrices = true;
            state.errors.getSilverPrices = '';
        },
        silverGetSilverPricesSuccess: (state, action: PayloadAction<TGetSilverPricesAPIResponse>) => {
            state.loadings.getSilverPrices = false;
            state.errors.getSilverPrices = '';
            state.buyAndSellSilver.silverPrices = action.payload;
        },
        silverGetSilverPricesFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getSilverPrices = false;
            if (action.payload) state.errors.getSilverPrices = action.payload;
        },
        silverCreateBuySilverTransactionAttempt: (state) => {
            state.loadings.createBuySilverTransaction = true;
            state.errors.createBuySilverTransaction = '';
        },
        silverCreateBuySilverTransactionSuccess: (state) => {
            state.loadings.createBuySilverTransaction = false;
            state.errors.createBuySilverTransaction = '';
        },
        silverCreateBuySilverTransactionFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createBuySilverTransaction = false;
            if (action.payload) state.errors.createBuySilverTransaction = action.payload;
        },
        silverCreateSellSilverTransactionAttempt: (state) => {
            state.loadings.createSellSilverTransaction = true;
            state.errors.createSellSilverTransaction = '';
        },
        silverCreateSellSilverTransactionSuccess: (state) => {
            state.loadings.createSellSilverTransaction = false;
            state.errors.createSellSilverTransaction = '';
        },
        silverCreateSellSilverTransactionFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createSellSilverTransaction = false;
            if (action.payload) state.errors.createSellSilverTransaction = action.payload;
        },
        silverGetTransactionReceiptAttempt: (state, _action: PayloadAction<{ transactionId: number }>) => {
            state.loadings.getTransactionReceipt = true;
            state.errors.getTransactionReceipt = '';
        },
        silverGetTransactionReceiptSuccess: (state) => {
            state.loadings.getTransactionReceipt = false;
            state.errors.getTransactionReceipt = '';
        },
        silverGetTransactionReceiptFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getTransactionReceipt = false;
            if (action.payload) state.errors.getTransactionReceipt = action.payload;
        },
        silverSetTransactionListingFilters: (state, action: PayloadAction<TGetSilverTransactionListingFilters>) => {
            state.buyAndSellSilver.transactionListing.filters = action.payload;
        },
        silverSetTransactionListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.buyAndSellSilver.transactionListing.index = index;
        },
        silverSetTransactionType: (state, action: PayloadAction<TransactionActionEnum>) => {
            state.buyAndSellSilver.transactionType = action.payload;
        },
        silverSetSelectedTransaction: (state, action: PayloadAction<TTransactionSilverListing | undefined>) => {
            state.buyAndSellSilver.selectedTransaction = action.payload;
        },

        // ! silver inventory
        silverGetInventoryTransactionListingAttempt: (state) => {
            state.loadings.getInventoryTransactionListing = true;
            state.errors.getInventoryTransactionListing = '';
        },
        silverGetInventoryTransactionListingSuccess: (state, action: PayloadAction<TGetInventoryTransactionListingAPIResponse>) => {
            state.loadings.getInventoryTransactionListing = false;
            state.errors.getInventoryTransactionListing = '';
            state.silverInventory.inventoryTransactionListing = {
                filters: state.silverInventory.inventoryTransactionListing.filters,
                ...action.payload,
            };
        },
        silverGetInventoryTransactionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getInventoryTransactionListing = false;
            if (action.payload) state.errors.getInventoryTransactionListing = action.payload;
        },
        silverGetInventoryListingAttempt: (state) => {
            state.loadings.getInventoryListing = true;
            state.errors.getInventoryListing = '';
        },
        silverGetInventoryListingSuccess: (state, action: PayloadAction<TGetInventoryListingAPIResponse>) => {
            state.loadings.getInventoryListing = false;
            state.errors.getInventoryListing = '';
            state.silverInventory.inventoryListing = {
                filters: state.silverInventory.inventoryListing.filters,
                selectedTab: state.silverInventory.inventoryListing.selectedTab,
                ...action.payload,
            };
        },
        silverGetInventoryListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getInventoryListing = false;
            if (action.payload) state.errors.getInventoryListing = action.payload;
        },
        silverCreateInventoryAttempt: (state) => {
            state.loadings.createInventory = true;
            state.errors.createInventory = '';
        },
        silverCreateInventorySuccess: (state) => {
            state.loadings.createInventory = false;
            state.errors.createInventory = '';
        },
        silverCreateInventoryFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createInventory = false;
            if (action.payload) state.errors.createInventory = action.payload;
        },
        silverTransferInventoryAttempt: (state) => {
            state.loadings.transferInventory = true;
            state.errors.transferInventory = '';
        },
        silverTransferInventorySuccess: (state) => {
            state.loadings.transferInventory = false;
            state.errors.transferInventory = '';
        },
        silverTransferInventoryFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.transferInventory = false;
            if (action.payload) state.errors.transferInventory = action.payload;
        },

        silverGetAllProductsAttempt: (state) => {
            state.loadings.getAllProducts = true;
            state.errors.getAllProducts = '';
        },
        silverGetAllProductsSuccess: (state, action: PayloadAction<TGetProductFilterAPIResponse>) => {
            state.loadings.getAllProducts = false;
            state.errors.getAllProducts = '';
            state.allProducts = action.payload.data;
        },
        silverGetAllProductsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getAllProducts = false;
            if (action.payload) state.errors.getAllProducts = action.payload;
        },
        silverGetAllInventoriesAttempt: (state) => {
            state.loadings.getAllInventories = true;
            state.errors.getAllInventories = '';
        },
        silverGetAllInventoriesSuccess: (state, action: PayloadAction<TGetInventoryFilterAPIResponse>) => {
            state.loadings.getAllInventories = false;
            state.errors.getAllInventories = '';
            state.allInventories = action.payload;
        },
        silverGetAllInventoriesFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getAllInventories = false;
            if (action.payload) state.errors.getAllInventories = action.payload;
        },
        silverGetAllSerialNumbersAttempt: (state) => {
            state.loadings.getAllSerialNumbers = true;
            state.errors.getAllSerialNumbers = '';
        },
        silverGetAllSerialNumbersSuccess: (state, action: PayloadAction<TGetSerialNumberFilterAPIResponse>) => {
            state.loadings.getAllSerialNumbers = false;
            state.errors.getAllSerialNumbers = '';
            state.allSerialNumbers = action.payload;
        },
        silverGetAllSerialNumbersFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getAllSerialNumbers = false;
            if (action.payload) state.errors.getAllSerialNumbers = action.payload;
        },
        silverGetBranchInventoriesAttempt: (state, _action: PayloadAction<{ branchCode: string }>) => {
            state.loadings.getBranchInventories = true;
            state.errors.getBranchInventories = '';
        },
        silverGetBranchInventoriesSuccess: (state, action: PayloadAction<TGetInventoryFilterAPIResponse>) => {
            state.loadings.getBranchInventories = false;
            state.errors.getBranchInventories = '';
            state.branchInventories = action.payload;
        },
        silverGetBranchInventoriesFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getBranchInventories = false;
            if (action.payload) state.errors.getBranchInventories = action.payload;
        },
        silverGetInventoryTransferListingAttempt: (state) => {
            state.loadings.getInventoryTransferListing = true;
            state.errors.getInventoryTransferListing = '';
        },
        silverGetInventoryTransferListingSuccess: (state, action: PayloadAction<TGetInventoryTransferListingAPIResponse>) => {
            state.loadings.getInventoryTransferListing = false;
            state.errors.getInventoryTransferListing = '';
            state.silverInventory.inventoryTransferListing = {
                filters: state.silverInventory.inventoryTransferListing.filters,
                ...action.payload,
            };
        },
        silverGetInventoryTransferListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getInventoryTransferListing = false;
            if (action.payload) state.errors.getInventoryTransferListing = action.payload;
        },
        // table filter and paginator
        silverSetInventoryTransactionListingFilters: (state, action: PayloadAction<TGetSilverInventoryTransactionListingFilters>) => {
            state.silverInventory.inventoryTransactionListing.filters = action.payload;
        },
        silverSetInventoryTransactionListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.silverInventory.inventoryTransactionListing.index = index;
        },
        silverSetInventoryListingFilters: (state, action: PayloadAction<TGetSilverInventoryListingFilters>) => {
            state.silverInventory.inventoryListing.filters = action.payload;
        },
        silverClearGetInventoryListingFilters: (state) => {
            state.silverInventory.inventoryListing.filters = initialState.silverInventory.inventoryListing.filters;
        },
        silverSetInventoryListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.silverInventory.inventoryListing.index = index;
        },
        silverSetSelectedInventoryTab: (state, action: PayloadAction<SilverInventorySelectedTab>) => {
            state.silverInventory.inventoryListing.selectedTab = action.payload;
        },
        silverSetInventoryTransfersListingFilters: (state, action: PayloadAction<TGetInventoryTransferListingFilters>) => {
            state.silverInventory.inventoryTransferListing.filters = action.payload;
        },
        silverClearGetinventoryTransferListingFilters: (state) => {
            state.silverInventory.inventoryTransferListing.filters = initialState.silverInventory.inventoryTransferListing.filters;
        },
        //params
        silverSetBuyOrSellSilverParams: (state, action: PayloadAction<TBuyOrSellMultipleSilverParams>) => {
            state.params.buyOrSellSilverParams = action.payload;
        },
        silverClearSellOrBuysilverParams: (state) => {
            state.params.buyOrSellSilverParams = initialState.params.buyOrSellSilverParams;
        },
        silverSetCreateInventoryParams: (state, action: PayloadAction<TCreateInventoryParamsV2>) => {
            state.params.createInventoryParams = action.payload;
        },
        silverClearCreateInventoryParams: (state) => {
            state.params.createInventoryParams = initialState.params.createInventoryParams;
        },
        silverSetTransferInventoryParams: (state, action: PayloadAction<TTransferInventoryParams>) => {
            state.params.transferInventoryParams = action.payload;
        },
        silverClearTransferInventoryParams: (state) => {
            state.params.transferInventoryParams = initialState.params.transferInventoryParams;
        },
        silverSetInventoryFilterParams: (state, action: PayloadAction<TGetInventoryFilterParams>) => {
            state.params.inventoryFilterParams = action.payload;
        },
        silverClearInventoryFilterParams: (state) => {
            state.params.inventoryFilterParams = initialState.params.inventoryFilterParams;
        },
        silverSetSerialNumberFilterParams: (state, action: PayloadAction<TGetSerialNumberFilterParams>) => {
            state.params.serialNumberFilterParams = action.payload;
        },
        silverClearSerialNumberFilterParams: (state) => {
            state.params.serialNumberFilterParams = initialState.params.serialNumberFilterParams;
        },
        // modals
        silverSetIsFormFilled: (state, action: PayloadAction<boolean>) => {
            state.modals.isFormFilled = action.payload;
        },
        silverSetIsConfirmationModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isConfirmationModalOpen = action.payload;
        },
        silverSetIsBuyOrSellSilverModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isBuyOrSellSilverModalOpen = action.payload;
        },
        silverSetIsTransactionDetailsModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isTransactionDetailsModalOpen = action.payload;
        },
        silverSetIsInventoryListingModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isInventoryListingModalOpen = action.payload;
        },
        silverSetIsCreateInventoryModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isCreateInventoryModalOpen = action.payload;
        },
        silverSetIsTransferInventoryModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isTransferInventoryModalOpen = action.payload;
        },
        silverExportInventoryTransactionListingAttempt: (state) => {
            state.loadings.exportInventoryTransactionListing = true;
            state.errors.exportInventoryTransactionListing = '';
        },
        silverExportInventoryTransactionListingSuccess: (state, action: PayloadAction<TInventorySilverTransactionListing[]>) => {
            state.loadings.exportInventoryTransactionListing = false;
            state.inventoryTransactionListingForExport = action.payload;
        },
        silverExportInventoryTransactionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportInventoryTransactionListing = false;
            if (action.payload) state.errors.exportInventoryTransactionListing = action.payload;
        },
        silverClearInventoryTransactionListingForExport: (state) => {
            state.inventoryTransactionListingForExport = initialState.inventoryTransactionListingForExport;
        },
        silverExportSilverTransactionListingAttempt: (state) => {
            state.loadings.exportSilverTransactionListing = true;
            state.errors.exportSilverTransactionListing = '';
        },
        silverExportSilverTransactionListingSuccess: (state, action: PayloadAction<TSilverTransactionListing[]>) => {
            state.loadings.exportSilverTransactionListing = false;
            state.silverTransactionListingForExport = action.payload;
        },
        silverExportSilverTransactionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportSilverTransactionListing = false;
            if (action.payload) state.errors.exportSilverTransactionListing = action.payload;
        },
        silverClearsilverTransactionListingForExport: (state) => {
            state.silverTransactionListingForExport = initialState.silverTransactionListingForExport;
        },
        silverSetTempUpdateInventoryTransactionState: (state, action: PayloadAction<TUpdateInventoryTransactionStatusReduxParams>) => {
            state.tempUpdateInventoryTransactionState = action.payload;
        },
        silverSetConfirmUpdateInventoryTransactionStatusModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.confirmUpdateInventoryTransactionStatusModalIsOpen = action.payload;
        },
        silverResetUpdateInventoryTransactionStatusState: (state) => {
            state.loadings.updateInventoryTransactionStatus = initialState.loadings.updateInventoryTransactionStatus;
            state.errors.updateInventoryTransactionStatus = initialState.errors.updateInventoryTransactionStatus;
        },
        silverUpdateInventoryTransactionStatusAttempt: (state, _action: PayloadAction<TUpdateInventoryTransactionStatusReduxParams>) => {
            state.loadings.updateInventoryTransactionStatus = true;
            state.errors.updateInventoryTransactionStatus = '';
        },
        silverUpdateInventoryTransactionStatusSuccess: (state) => {
            state.loadings.updateInventoryTransactionStatus = false;
        },
        silverUpdateInventoryTransactionStatusFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.updateInventoryTransactionStatus = false;
            if (action.payload) state.errors.updateInventoryTransactionStatus = action.payload;
        },
        silverSetTempUpdateTransactionState: (state, action: PayloadAction<TUpdateTransactionStatusReduxParams>) => {
            state.tempUpdateTransactionState = action.payload;
        },
        silverSetConfirmUpdateTransactionStatusModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.confirmUpdateTransactionStatusModalIsOpen = action.payload;
        },
        silverResetUpdateTransactionStatusState: (state) => {
            state.loadings.updateTransactionStatus = initialState.loadings.updateTransactionStatus;
            state.errors.updateTransactionStatus = initialState.errors.updateTransactionStatus;
        },
        silverUpdateTransactionStatusAttempt: (state, _action: PayloadAction<TUpdateTransactionStatusReduxParams>) => {
            state.loadings.updateTransactionStatus = true;
            state.errors.updateTransactionStatus = '';
        },
        silverUpdateTransactionStatusSuccess: (state) => {
            state.loadings.updateTransactionStatus = false;
        },
        silverUpdateTransactionStatusFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.updateTransactionStatus = false;
            if (action.payload) state.errors.updateTransactionStatus = action.payload;
        },
        silverGetTotalNetValueOrQuantityAttempt: (state, _action: PayloadAction<TGetTotalNetValueOrQuantityReduxParams>) => {
            state.loadings.getTotalNetValueOrQuantity = true;
            state.errors.getTotalNetValueOrQuantity = '';
        },

        silverGetTotalNetValueOrQuantitySuccess: (state, action: PayloadAction<{ totalNetValue?: string, quantity?: string, index: number }>) => {
            state.loadings.getTotalNetValueOrQuantity = false;

            if (action.payload.quantity) state.params.buyOrSellSilverParams.products[action.payload.index].amount = action.payload.quantity;
            if (action.payload.totalNetValue) state.params.buyOrSellSilverParams.products[action.payload.index].totalNetValue = action.payload.totalNetValue;
        },
        silverGetTotalNetValueOrQuantityFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getTotalNetValueOrQuantity = false;
            if (action.payload) state.errors.getTotalNetValueOrQuantity = action.payload;
        },

        silverCreateTransactionDraftAttempt: (state, _action: PayloadAction<TCreateTransactionDraftReduxParams>) => {
            state.loadings.createTransactionDraft = true;
            state.errors.createTransactionDraft = '';
        },
        silverCreateTransactionDraftSuccess: (state, action: PayloadAction<TCreateSilverTransactionDraftResponse>) => {
            state.createTransactionSummary = action.payload;
            state.loadings.createTransactionDraft = false;
            state.errors.createTransactionDraft = '';
        },

        silverCreateTransactionDraftFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createTransactionDraft = false;
            if (action.payload) state.errors.createTransactionDraft = action.payload;
        },

        silverSetTempUpdateInventoryTransferState: (state, action: PayloadAction<TupdateTransferStatusReduxParams>) => {
            state.tempUpdateInventoryTransfersState = action.payload;
        },
        silverSetConfirmUpdateInventoryTransferStatusModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.confirmUpdateInventoryTransfersStatusModalIsOpen = action.payload;
        },
        silverResetUpdateInventoryTransferStatusState: (state) => {
            state.loadings.updateInventoryTransferStatus = initialState.loadings.updateInventoryTransferStatus;
            state.errors.updateInventoryTransferStatus = initialState.errors.updateInventoryTransferStatus;
        },
        silverUpdateInventoryTransferStatusAttempt: (state, _action: PayloadAction<TupdateTransferStatusReduxParams>) => {
            state.loadings.updateInventoryTransferStatus = true;
            state.errors.updateInventoryTransferStatus = '';
        },
        silverUpdateInventoryTransferStatusSuccess: (state) => {
            state.loadings.updateInventoryTransferStatus = false;
        },
        silverUpdateInventoryTransferStatusFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.updateInventoryTransferStatus = false;
            if (action.payload) state.errors.updateInventoryTransferStatus = action.payload;
        },
        silverSetTransactionProductListingModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.transactionProductListingModalIsOpen = action.payload;
        },
        silverSetSerialNumberListingFilters: (state, action: PayloadAction<TGetSerialNumberListingFilter>) => {
            state.serialNumber.serialNumberListing.filters = action.payload;
        },
        silverSetSerialNumberListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.serialNumber.serialNumberListing.index = index;
        },
        silverGetSerialNumberListingAttempt: (state) => {
            state.loadings.getSerialNumberListing = true;
            state.errors.getSerialNumberListing = '';
        },
        silverGetSerialNumberListingSuccess: (state, action: PayloadAction<TGetSerialNumberListingAPIResponse>) => {
            state.loadings.getSerialNumberListing = false;
            state.serialNumber.serialNumberListing = {
                filters: state.serialNumber.serialNumberListing.filters,
                ...action.payload,
            };
        },
        silverGetSerialNumberListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getSerialNumberListing = false;
            if (action.payload) state.errors.getSerialNumberListing = '';
        },
        silverGetTransactionPricePerGramValueAttempt: (state, _action: PayloadAction<TGetSilverTransactionPricePerGramValueReduxParam>) => {
            state.loadings.getTransactionPricePerGramValue = true;
            state.errors.getTransactionPricePerGramValue = '';
        },
        silverGetTransactionPricePerGramValueSuccess: (state, action: PayloadAction<{pricePerGram: string | null, index: number}>) => {
            state.loadings.getTransactionPricePerGramValue = false;
            if (action.payload) state.params.buyOrSellSilverParams.products[action.payload.index].silverAmountMYR = action.payload.pricePerGram || '';
        },
        silverGetTransactionPricePerGramValueFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getTransactionPricePerGramValue = false;
            if (action.payload) state.errors.getTransactionPricePerGramValue = action.payload;
        },
        silverResetTransactionPricePerGramValue: (state, action: PayloadAction<{ index: number}>) => {
            state.params.buyOrSellSilverParams.products[action.payload.index].silverAmountMYR = '';
        },
        // silver Inventory Report
        silverSetSelectedsilverInventoryReportTab: (state, action: PayloadAction<ReportsSilverInventorySelectedTab>) => {
            state.silverInventoryReport.selectedTab = action.payload;
        },
        silverSetInventoryReceivedListingFilters: (state, action: PayloadAction<ReportSilverInventoryReceivedListingFilters>) => {
            state.silverInventoryReport.inventoryReceived.filters = action.payload;
        },
        silverClearInventoryReceivedListingFilters: (state) => {
            state.silverInventoryReport.inventoryReceived.filters = initialState.silverInventoryReport.inventoryReceived.filters;
        },
        silverGetSilverInventoryReportReceivedListingAttempt: (state) => {
            state.loadings.getSilverInventoryReportReceivedListing = true;
            state.errors.getSilverInventoryReportReceivedListing = '';
        },
        silverGetSilverInventoryReportReceivedListingSuccess: (state, action: PayloadAction<TGetInventoryTransactionListingAPIResponse>) => {
            state.loadings.getSilverInventoryReportReceivedListing = false;
            state.errors.getSilverInventoryReportReceivedListing = '';
            state.silverInventoryReport.inventoryReceived = {
                filters: state.silverInventoryReport.inventoryReceived.filters,
                ...action.payload,
            };
        },
        silverGetSilverInventoryReportReceivedListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getSilverInventoryReportReceivedListing = false;
            if (action.payload) state.errors.getSilverInventoryReportReceivedListing = '';
        },
        silverSetsilverInventoryReportReceivedListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.silverInventoryReport.inventoryReceived.index = index;
        },
        silverExportInventoryReceivedListingAttempt: (state) => {
            state.loadings.exportInventoryReceivedListing = true;
            state.errors.exportInventoryReceivedListing = '';
        },
        silverExportInventoryReceivedListingSuccess: (state, action: PayloadAction<TInventorySilverTransactionListing[]>) => {
            state.loadings.exportInventoryReceivedListing = false;
            state.inventoryReceivedListingForExport = action.payload;
        },
        silverExportInventoryReceivedListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportInventoryReceivedListing = false;
            if (action.payload) state.errors.exportInventoryReceivedListing = action.payload;
        },
        silverClearInventoryReceivedListingForExport: (state) => {
            state.inventoryReceivedListingForExport = initialState.inventoryReceivedListingForExport;
        },

        silverSetInventoryDeliveredListingFilters: (state, action: PayloadAction<ReportSilverInventoryReceivedListingFilters>) => {
            state.silverInventoryReport.inventoryDelivered.filters = action.payload;
        },
        silverGetSilverInventoryReportDeliveredListingAttempt: (state) => {
            state.loadings.getSilverInventoryReportDeliveredListing = true;
            state.errors.getSilverInventoryReportDeliveredListing = '';
        },
        silverGetSilverInventoryReportDeliveredListingSuccess: (state, action: PayloadAction<TGetInventoryTransactionListingAPIResponse>) => {
            state.loadings.getSilverInventoryReportDeliveredListing = false;
            state.errors.getSilverInventoryReportDeliveredListing = '';
            state.silverInventoryReport.inventoryDelivered = {
                filters: state.silverInventoryReport.inventoryDelivered.filters,
                ...action.payload,
            };
        },
        silverGetSilverInventoryReportDeliveredListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getSilverInventoryReportDeliveredListing = false;
            if (action.payload) state.errors.getSilverInventoryReportDeliveredListing = '';
        },
        silverSetsilverInventoryReportDeliveredListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.silverInventoryReport.inventoryDelivered.index = index;
        },
        silverExportInventoryDeliveredListingAttempt: (state) => {
            state.loadings.exportInventoryDeliveredListing = true;
            state.errors.exportInventoryDeliveredListing = '';
        },
        silverExportInventoryDeliveredListingSuccess: (state, action: PayloadAction<TInventorySilverTransactionListing[]>) => {
            state.loadings.exportInventoryDeliveredListing = false;
            state.inventoryDeliveredListingForExport = action.payload;
        },
        silverExportInventoryDeliveredListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportInventoryDeliveredListing = false;
            if (action.payload) state.errors.exportInventoryDeliveredListing = action.payload;
        },
        silverClearInventoryDeliveredListingForExport: (state) => {
            state.inventoryDeliveredListingForExport = initialState.inventoryDeliveredListingForExport;
        },
        silverGetBranchInventoryListingAttempt: (state) => {
            state.loadings.getBranchInventoryListing = true;
            state.errors.getBranchInventoryListing = '';
        },
        silverGetBranchInventoryListingSuccess: (state, action: PayloadAction<TGetInventoryListingAPIResponse>) => {
            state.loadings.getBranchInventoryListing = false;
            state.silverInventoryReport.branchInventory = {
                filters: state.silverInventoryReport.branchInventory.filters,
                ...action.payload,
            };
        },
        silverGetBranchInventoryListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getBranchInventoryListing = false;
            if (action.payload) state.errors.getBranchInventoryListing = action.payload;
        },
        silverSetBranchInventoryListingFilters: (state, action: PayloadAction<{branchCode: string}>) => {
            state.silverInventoryReport.branchInventory.filters = action.payload;
        },
        silverClearBranchInventoryListingFilters: (state) => {
            state.silverInventoryReport.branchInventory.filters = initialState.silverInventoryReport.branchInventory.filters;
        },
        silverSetIsCreateSilverConversionModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isCreateSilverConversionModalOpen = action.payload;
        },
        silverSetsilverConversionIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.silverConversion.index = index;
        },
        silverSetsilverConversionFilters: (state, action: PayloadAction<TSilverConversionFiltersRedux>) => {
            state.silverConversion.filters = action.payload;
        },
        silverGetSilverConversionListingAttempt: (state) => {
            state.loadings.getSilverConversionListing = true;
            state.errors.getSilverConversionListing = '';
        },
        silverGetSilverConversionListingSuccess: (state, action: PayloadAction<TSilverConversionListingAPIResponse>) => {
            state.loadings.getSilverConversionListing = false;
            state.silverConversion = {
                filters: state.silverConversion.filters,
                ...action.payload,
            };
        },
        silverGetSilverConversionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getSilverConversionListing = false;
            if (action.payload) state.errors.getSilverConversionListing = action.payload;
        },
        silverCreateSilverConversionAttempt: (state, _action: PayloadAction<TCreateSilverConversionReduxParams>) => {
            state.loadings.createSilverConversion = true;
            state.errors.createSilverConversion = '';
        },
        silverCreateSilverConversionSuccess: (state) => {
            state.loadings.createSilverConversion = false;
            state.errors.createSilverConversion = '';
        },
        silverCreateSilverConversionFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createSilverConversion = false;
            if (action.payload) state.errors.createSilverConversion = action.payload;
        },
        silverGetSilverConversionAmountAttempt: (state, _action: PayloadAction<TGetSilverConversionAmountParams>) => {
            state.loadings.getSilverConversionAmount = true;
            state.errors.getSilverConversionAmount = '';
        },
        silverGetSilverConversionAmountSuccess: (state, action: PayloadAction<number>) => {
            state.loadings.getSilverConversionAmount = false;
            if (action.payload) state.silverConversion.silverConversionAmount = action.payload;
        },
        silverGetSilverConversionAmountFailure: (state, action: PayloadAction<string | null>) => {
            state.loadings.getSilverConversionAmount = false;
            if (action.payload) state.errors.getSilverConversionAmount = action.payload;
        },
        silverResetsilverConversionAmount: (state) => {
            state.silverConversion.silverConversionAmount = initialState.silverConversion.silverConversionAmount;
        },

        silverCreateEarmarkConversionAttempt: (state, _action: PayloadAction<TCreateEarmarkConversionReduxParams>) => {
            state.loadings.createEarmarkConversion = true;
            state.errors.createEarmarkConversion = '';
        },

        silverCreateEarmarkConversionSuccess: (state) => {
            state.loadings.createEarmarkConversion = false;
            state.errors.createEarmarkConversion = '';
        },

        silverCreateEarmarkConversionFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createEarmarkConversion = false;
            if (action.payload) state.errors.createEarmarkConversion = action.payload;
        },

        silverGetEarmarkConversionListingAttempt: (state, action: PayloadAction<TGetEarmarkConversionListingReduxParams>) => {
            state.loadings.getEarmarkConversionListing = true;
            state.errors.getEarmarkConversionListing = '';
        },
        silverGetEarmarkConversionListingSuccess: (state, action: PayloadAction<TGetEarmarkConversionListingAPIResponse>) => {
            state.loadings.getEarmarkConversionListing = false;
            state.errors.getEarmarkConversionListing = '';
            state.earmarkConversionListing = {
                filters: state.earmarkConversionListing.filters,
                ...action.payload,
            };
        },
        silverGetEarmarkConversionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getEarmarkConversionListing = false;
            if (action.payload) state.errors.getEarmarkConversionListing = action.payload;
        },
        silverSetEarmarkConversionListingFilters: (state, action: PayloadAction<TGetEarmarkConversionListingFilters>) => {
            state.earmarkConversionListing.filters = action.payload;
        },
        silverSetEarmarkConversionListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.earmarkConversionListing.index = action.payload.index;
        },
        silverUpdateEarmarkDetailsAttempt: (state, _action: PayloadAction<TUpdateEarmarkDetailsReduxParam>) => {
            state.loadings.updateEarmarkDetails = true;
            state.errors.updateEarmarkDetails = '';
        },
        silverUpdateEarmarkDetailsSuccess: (state) => {
            state.loadings.updateEarmarkDetails = false;
        },
        silverUpdateEarmarkDetailsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.updateEarmarkDetails = false;
            if (action.payload) state.errors.updateEarmarkDetails = action.payload;
        },
        silverGetInventoryUsageAttempt: (state, _action: PayloadAction<{ branchId?: number } | undefined>) => {
            state.loadings.getInventoryUsage = true;
            state.errors.getInventoryUsage = '';
        },
        silverGetInventoryUsageSuccess: (state, action: PayloadAction<TGetInventoryUsageAPIResponse>) => {
            state.loadings.getInventoryUsage = false;
            state.errors.getInventoryUsage = '';
            state.inventoryUsage = {
                ...state.inventoryUsage,
                ...action.payload,
            };
        },
        silverGetInventoryUsageFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getInventoryUsage = false;
            if (action.payload) state.errors.getInventoryUsage = action.payload;
        },
    },
});

export default {
    actions: silverSlice.actions,
    reducers: silverSlice.reducer,
};
